import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { loadContacts } from '../Action/api';

// Async thunk to fetch contacts from API
export const fetchContacts = createAsyncThunk(
  'contacts/fetchContacts',
  async ({ country, monthYear }, { rejectWithValue }) => {
    try {
      console.log('Redux: Fetching contacts from API...', { country, monthYear });
      const response = await loadContacts(country, monthYear);
      
      if (response.success && response.data) {
        console.log('Redux: Successfully fetched', response.data.length, 'contacts');
        return {
          contacts: response.data,
          count: response.count,
          message: response.message,
          country,
          monthYear,
        };
      } else {
        return rejectWithValue(response.message || 'Failed to fetch contacts');
      }
    } catch (error) {
      console.error('Redux: Error fetching contacts:', error);
      return rejectWithValue(error.message || 'An error occurred while fetching contacts');
    }
  }
);

const contactsSlice = createSlice({
  name: 'contacts',
  initialState: {
    data: [], // Array of contacts
    loading: false,
    error: null,
    lastFetched: null, // Timestamp of last fetch
    currentCountry: 'India', // Currently selected country
    currentMonthYear: null, // Currently selected month/year
    stats: {
      totalContacts: 0,
      personContacts: 0,
      companyContacts: 0,
      thisMonth: 0,
    },
  },
  reducers: {
    // Update a single contact in the store (for when user edits a contact)
    updateContactInStore: (state, action) => {
      const updatedContact = action.payload;
      const index = state.data.findIndex(contact => contact.dateTimeStamp === updatedContact.dateTimeStamp);
      
      if (index !== -1) {
        // Update the contact
        state.data[index] = {
          ...state.data[index],
          ...updatedContact,
        };
        
        // Recalculate stats
        const total = state.data.length;
        const persons = state.data.filter(c => c.contactType === 'person').length;
        const companies = state.data.filter(c => c.contactType === 'company').length;
        const now = new Date();
        const thisMonth = state.data.filter(c => {
          const contactDate = new Date(c.createdAt);
          return contactDate.getMonth() === now.getMonth() && contactDate.getFullYear() === now.getFullYear();
        }).length;
        
        state.stats = {
          totalContacts: total,
          personContacts: persons,
          companyContacts: companies,
          thisMonth: thisMonth,
        };
        
        console.log('Redux: Contact updated in store:', updatedContact.dateTimeStamp);
      }
    },
    
    // Add a new contact to the store
    addContactToStore: (state, action) => {
      const newContact = action.payload;
      state.data.unshift(newContact); // Add to beginning
      
      // Recalculate stats
      const total = state.data.length;
      const persons = state.data.filter(c => c.contactType === 'person').length;
      const companies = state.data.filter(c => c.contactType === 'company').length;
      const now = new Date();
      const thisMonth = state.data.filter(c => {
        const contactDate = new Date(c.createdAt);
        return contactDate.getMonth() === now.getMonth() && contactDate.getFullYear() === now.getFullYear();
      }).length;
      
      state.stats = {
        totalContacts: total,
        personContacts: persons,
        companyContacts: companies,
        thisMonth: thisMonth,
      };
      
      console.log('Redux: New contact added to store');
    },
    
    // Delete a contact from the store
    deleteContactFromStore: (state, action) => {
      const dateTimeStamp = action.payload;
      const index = state.data.findIndex(contact => contact.dateTimeStamp === dateTimeStamp);
      
      if (index !== -1) {
        state.data.splice(index, 1); // Remove from array
        
        // Recalculate stats
        const total = state.data.length;
        const persons = state.data.filter(c => c.contactType === 'person').length;
        const companies = state.data.filter(c => c.contactType === 'company').length;
        const now = new Date();
        const thisMonth = state.data.filter(c => {
          const contactDate = new Date(c.createdAt);
          return contactDate.getMonth() === now.getMonth() && contactDate.getFullYear() === now.getFullYear();
        }).length;
        
        state.stats = {
          totalContacts: total,
          personContacts: persons,
          companyContacts: companies,
          thisMonth: thisMonth,
        };
        
        console.log('Redux: Contact deleted from store:', dateTimeStamp);
      }
    },
    
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    
    // Force refresh flag (to trigger API call on refresh button)
    resetLastFetched: (state) => {
      state.lastFetched = null;
    },
    
    // Set current filters
    setCurrentFilters: (state, action) => {
      state.currentCountry = action.payload.country;
      state.currentMonthYear = action.payload.monthYear;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchContacts pending
      .addCase(fetchContacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // Handle fetchContacts fulfilled
      .addCase(fetchContacts.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.contacts;
        state.lastFetched = new Date().toISOString();
        state.currentCountry = action.payload.country;
        state.currentMonthYear = action.payload.monthYear;
        
        // Calculate stats
        const total = action.payload.contacts.length;
        const persons = action.payload.contacts.filter(c => c.contactType === 'person').length;
        const companies = action.payload.contacts.filter(c => c.contactType === 'company').length;
        const now = new Date();
        const thisMonth = action.payload.contacts.filter(c => {
          const contactDate = new Date(c.createdAt);
          return contactDate.getMonth() === now.getMonth() && contactDate.getFullYear() === now.getFullYear();
        }).length;
        
        state.stats = {
          totalContacts: total,
          personContacts: persons,
          companyContacts: companies,
          thisMonth: thisMonth,
        };
        
        state.error = null;
      })
      // Handle fetchContacts rejected
      .addCase(fetchContacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch contacts';
      });
  },
});

export const { 
  updateContactInStore, 
  addContactToStore, 
  deleteContactFromStore,
  clearError, 
  resetLastFetched,
  setCurrentFilters 
} = contactsSlice.actions;

export default contactsSlice.reducer;

// Selectors
export const selectAllContacts = (state) => state.contacts.data;
export const selectContactsLoading = (state) => state.contacts.loading;
export const selectContactsError = (state) => state.contacts.error;
export const selectContactsStats = (state) => state.contacts.stats;
export const selectLastFetched = (state) => state.contacts.lastFetched;
export const selectCurrentCountry = (state) => state.contacts.currentCountry;
export const selectCurrentMonthYear = (state) => state.contacts.currentMonthYear;
export const selectContactByDateTimeStamp = (dateTimeStamp) => (state) => 
  state.contacts.data.find(contact => contact.dateTimeStamp === dateTimeStamp);
